"use client";

import { useRef, useState } from "react";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";
import { ArrowUp } from "lucide-react";

/**
 * ScrollToTop — floating button that appears after 40% scroll.
 * Animated entrance/exit. Smooth scrolls to top on click.
 * Positioned bottom-right, above the sticky mobile CTA on mobile.
 */
export default function ScrollToTop() {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);

  useGSAP(() => {
    if (!btnRef.current) return;

    ScrollTrigger.create({
      trigger: document.documentElement,
      start: "35% top",
      end: "95% top",
      onEnter: () => setVisible(true),
      onLeave: () => setVisible(false),
      onEnterBack: () => setVisible(true),
      onLeaveBack: () => setVisible(false),
    });
  });

  const scrollToTop = () => {
    // Prefer Lenis (single scroll engine, avoids competing with smooth scroll).
    const lenis = typeof window !== "undefined" ? window.__lenis : null;
    if (lenis) {
      lenis.scrollTo(0, { duration: 1.2 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <button
      ref={btnRef}
      onClick={scrollToTop}
      aria-label="Scroll to top"
      className="fixed bottom-6 right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-border-medium bg-bg-elevated/90 text-text-secondary backdrop-blur-md transition-all duration-300 hover:border-brand-cyan/40 hover:text-white lg:bottom-8 lg:right-8 lg:h-11 lg:w-11"
      style={{
        transform: visible ? "translateY(0) scale(1)" : "translateY(20px) scale(0.8)",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <ArrowUp size={16} />
    </button>
  );
}
